import {HttpErrorResponse} from '@angular/common/http';

export class BusinessError {
    status: number;
    error: BusinessErrorCode;
    errorDescription?: string;
    additionalParams: {[key: string]: any} = {};
    requestId?: string;
}

export function toBusinessOrNull(errorResponse: HttpErrorResponse): BusinessError | null {
    const errorCode = BusinessErrorCode[errorResponse.error?.error as BusinessErrorCode];
    if (errorCode) {
        const be = new BusinessError();
        be.status = errorResponse.status;
        be.additionalParams = errorResponse.error.additionalParams;
        be.errorDescription = errorResponse.error.errorDescription;
        be.error = errorCode;
        be.requestId = errorResponse.error.requestId;
        return be;
    } else {
        return null;
    }
}

export function toBusiness(errorResponse: HttpErrorResponse): BusinessError {
    const be = toBusinessOrNull(errorResponse);
    if (!be) {
        console.error('Failed to construct BusinessError, rethrowing...');
        throw errorResponse;
    }
    return be;
}

export enum BusinessErrorCode {
    // coid error codes
    IIN_ALREADY_EXISTS = 'IIN_ALREADY_EXISTS',
    PROVIDER_ALREADY_EXISTS = 'PROVIDER_ALREADY_EXISTS',
    GBDFL_PERSON_INVALID_STATUS = 'GBDFL_PERSON_INVALID_STATUS',
    TARIFFS_NOT_SPECIFIED = 'TARIFFS_NOT_SPECIFIED',
    IIN_NOT_FOUND = 'IIN_NOT_FOUND',
    IIN_BLOCKED = 'IIN_BLOCKED',
    PHONE_BELONGS_TO_ANOTHER_IIN_IN_BMG = 'PHONE_BELONGS_TO_ANOTHER_IIN_IN_BMG',
    TARIFF_LIMIT_EXCEEDED = 'TARIFF_LIMIT_EXCEEDED',
    LIVENESS_VENDOR_TARIFF_LIMIT_EXCEEDED = 'LIVENESS_VENDOR_TARIFF_LIMIT_EXCEEDED',
    LIVENESS_VENDOR_TARIFF_NOT_SPECIFIED = 'LIVENESS_VENDOR_TARIFF_NOT_SPECIFIED',
    IDENTITY_VENDOR_TARIFF_LIMIT_EXCEEDED = 'IDENTITY_VENDOR_TARIFF_LIMIT_EXCEEDED',
    IDENTITY_VENDOR_TARIFF_NOT_SPECIFIED = 'IDENTITY_VENDOR_TARIFF_NOT_SPECIFIED',
    REPORT_ROWS_LIMIT_EXCEEDED = 'REPORT_ROWS_LIMIT_EXCEEDED',
    ORGANIZATION_CONTRACT_PERIOD_OVERLAPS_ARCHIVED = 'ORGANIZATION_CONTRACT_PERIOD_OVERLAPS_ARCHIVED',
    ORGANIZATION_NO_ACTIVE_COID_CONTRACT = 'ORGANIZATION_NO_ACTIVE_COID_CONTRACT',
    AUTHORITY_MATRIX_NO_ACTIVE_CLIENT = 'AUTHORITY_MATRIX_NO_ACTIVE_CLIENT',
    AUTHORITY_MATRIX_HEAD_NOT_FOUND = 'AUTHORITY_MATRIX_HEAD_NOT_FOUND',
    AUTHORITY_MATRIX_HEAD_PHONE_NOT_FOUND = 'AUTHORITY_MATRIX_HEAD_PHONE_NOT_FOUND',


    // auth error codes
    AUTH_SESSION_ALREADY_COMPLETED = 'AUTH_SESSION_ALREADY_COMPLETED',
    CONFIRMATION_CODE_RATE_LIMIT_EXCEEDED = 'CONFIRMATION_CODE_RATE_LIMIT_EXCEEDED',
    SMS_PER_SESSION_LIMIT_EXCEEDED = 'SMS_PER_SESSION_LIMIT_EXCEEDED',
    GBDUL_ORGANIZATION_NOT_FOUND = 'GBDUL_ORGANIZATION_NOT_FOUND',
    GBDUL_ORGANIZATION_HAS_DIFFERENT_LEADER = 'GBDUL_ORGANIZATION_HAS_DIFFERENT_LEADER',
    GBDUL_IIN_NOT_LEADER = 'GBDUL_IIN_NOT_LEADER',
    EMPLOYEE_PERMISSION_DENIED = 'EMPLOYEE_PERMISSION_DENIED',
    EMPLOYEE_IS_NOT_IN_ORGANIZATION = 'EMPLOYEE_IS_NOT_IN_ORGANIZATION',
    GBDFL_PHOTO_NOT_FOUND = 'GBDFL_PHOTO_NOT_FOUND',
    GOV_SERVICE_UNAVAILABLE = 'GOV_SERVICE_UNAVAILABLE',
    GBDFL_PERSON_UNDER_18_YEARS = 'GBDFL_PERSON_UNDER_18_YEARS',
    DOCUMENT_NOT_FOUND_IN_TEST_DB = 'DOCUMENT_NOT_FOUND_IN_TEST_DB',
    DOCUMENT_NOT_FOUND = 'DOCUMENT_NOT_FOUND',
    LIVENESS_QR_EXPIRED = 'LIVENESS_QR_EXPIRED',

    ESIGN_WRONG_PASSWORD = 'ESIGN_WRONG_PASSWORD',
    ESIGN_IS_BLOCKED = 'ESIGN_IS_BLOCKED',
    ESIGN_CERTEX_CERTIFICATE_AUTHORITY_ERROR = 'ESIGN_CERTEX_CERTIFICATE_AUTHORITY_ERROR',
    ESIGN_CERTEX_UNAVAILABLE = 'ESIGN_CERTEX_UNAVAILABLE',
    ESIGN_INVALID_XML = 'ESIGN_INVALID_XML',

    // oauth error codes
    access_denied = 'access_denied',

    // digital tenge platform
    BIN_NOT_FOUND = 'BIN_NOT_FOUND',
    AMOUNT_EXCEEDS_AVAILABLE_TOKENS = 'AMOUNT_EXCEEDS_AVAILABLE_TOKENS',
    INVALID_CSV_FILE_CONTENT = 'INVALID_CSV_FILE_CONTENT',
    NODE_ID_NOT_FOUND = 'NODE_ID_NOT_FOUND',

    // gov_adapter
    OLD_PASSWORD_INCORRECT = 'OLD_PASSWORD_INCORRECT',
    NEW_PASSWORD_EQUALS_LAST = 'NEW_PASSWORD_EQUALS_LAST',
    USER_BLOCKED = 'USER_BLOCKED',

    // backoffice
    REPORT_LIMIT_EXCEEDED = 'REPORT_LIMIT_EXCEEDED',
    REPORT_ENTITY_MAX_COUNT_EXCEEDED = 'REPORT_ENTITY_MAX_COUNT_EXCEEDED',
    TARIFF_WITH_SAME_CODE_ALREADY_EXISTS = 'TARIFF_WITH_SAME_CODE_ALREADY_EXISTS',
    TARIFF_WITH_SAME_PARAMETERS_ALREADY_EXISTS = 'TARIFF_WITH_SAME_PARAMETERS_ALREADY_EXISTS',
    TARIFF_PLAN_WITH_SAME_NAME_ALREADY_EXISTS = 'TARIFF_PLAN_WITH_SAME_NAME_ALREADY_EXISTS',
    TARIFF_IS_IN_USE = 'TARIFF_IS_IN_USE',
    TARIFF_PLAN_IS_IN_USE = 'TARIFF_PLAN_IS_IN_USE',
    LIQUIDITY_NOT_DEFINED = 'LIQUIDITY_NOT_DEFINED',
    START_BALANCE_NOT_FINALIZED = 'START_BALANCE_NOT_FINALIZED',
    DISPUTE_TRANSACTION_NOT_COMMITTED = 'DISPUTE_TRANSACTION_NOT_COMMITTED',
    DISPUTE_TRANSACTION_NOT_DISPUTABLE = 'DISPUTE_TRANSACTION_NOT_DISPUTABLE',
    DISPUTE_TRANSACTION_REFUNDED = 'DISPUTE_TRANSACTION_REFUNDED',
    DISPUTE_ALREADY_EXISTS = 'DISPUTE_ALREADY_EXISTS',
    DISPUTE_FILING_PERIOD_EXPIRED = 'DISPUTE_FILING_PERIOD_EXPIRED',
    NET_POSITION_LIMIT_REACHED = 'NET_POSITION_LIMIT_REACHED',
    REFUND_AMOUNT_EXCEEDED = 'REFUND_AMOUNT_EXCEEDED',

    //ANTIFRAUD
    AF_ACCESS_DENIED = 'AF_ACCESS_DENIED',
    AF_FORBIDDEN = 'AF_FORBIDDEN',
    AF_ACCESS_DENIED_DELETE_INCIDENT = 'AF_ACCESS_DENIED_DELETE_INCIDENT',
    AF_FAILED_PASSWORD_UPDATE = 'AF_FAILED_PASSWORD_UPDATE',
    AF_CURRENT_PASSWORD_INVALID = 'AF_CURRENT_PASSWORD_INVALID',
    AF_PASSWORD_CHANGE_REQUIRED = 'AF_PASSWORD_CHANGE_REQUIRED',
    AF_PASSWORD_EXPIRED = 'AF_PASSWORD_EXPIRED',
    AF_IDENTIFICATION_NUMBER_IMMUTABLE = 'AF_IDENTIFICATION_NUMBER_IMMUTABLE',
    AF_REPORT_EXPORT_ACCESSES_DENIED = 'AF_REPORT_EXPORT_ACCESSES_DENIED',
    AF_REPORT_IMPORT_ACCESSES_DENIED = 'AF_REPORT_IMPORT_ACCESSES_DENIED',
    AF_SUBJECT_ALREADY_EXISTS = 'AF_SUBJECT_ALREADY_EXISTS',
    AF_SUBJECT_ACCESS_DENIED = 'AF_SUBJECT_ACCESS_DENIED',
    AF_USER_DISABLED = 'AF_USER_DISABLED',
    AF_USER_EMAIL_ALREADY_EXISTS = 'AF_USER_EMAIL_ALREADY_EXISTS',
    AF_USER_USERNAME_ALREADY_EXISTS = 'AF_USER_USERNAME_ALREADY_EXISTS',
    AF_USER_ALREADY_EXISTS = 'AF_USER_ALREADY_EXISTS',
    AF_THERE_IS_NO_RECORDS = 'AF_THERE_IS_NO_RECORDS',
    AF_FILE_HAS_VIRUS = 'AF_FILE_HAS_VIRUS',
    AF_FILE_WRONG_EXTENSION = 'AF_FILE_WRONG_EXTENSION',
    AF_ORGANIZATION_BIN_ALREADY_EXISTS = 'AF_ORGANIZATION_BIN_ALREADY_EXISTS',
    AF_ORGANIZATION_DELETED = 'AF_ORGANIZATION_DELETED',
    AF_ORGANIZATION_DELETED_NOT_ALLOWED = 'AF_ORGANIZATION_DELETED_NOT_ALLOWED',
    AF_ORGANIZATION_DELETE_DISABLED = 'AF_ORGANIZATION_DELETE_DISABLED',
    AF_ORGANIZATION_DOCUMENT_TYPE_INVALID = 'AF_ORGANIZATION_DOCUMENT_TYPE_INVALID',
    AF_ORGANIZATION_DOCUMENT_DELETED = 'AF_ORGANIZATION_DOCUMENT_DELETED',
    AF_REFERENCE_BOOK_BIN_READONLY = 'AF_REFERENCE_BOOK_BIN_READONLY',
    AF_IMPORT_ERROR = 'AF_IMPORT_ERROR',
    AF_EXPORT_ERROR = 'AF_EXPORT_ERROR',
    AF_PASSWORD_RECOVERY_USER_NOT_FOUND = 'AF_PASSWORD_RECOVERY_USER_NOT_FOUND',
    AF_PASSWORD_RECOVERY_EMAIL_NOT_AVAILABLE = 'AF_PASSWORD_RECOVERY_EMAIL_NOT_AVAILABLE',
    AF_PASSWORD_RECOVERY_PHONE_NOT_AVAILABLE = 'AF_PASSWORD_RECOVERY_PHONE_NOT_AVAILABLE',
    AF_PASSWORD_RECOVERY_RATE_LIMITED = 'AF_PASSWORD_RECOVERY_RATE_LIMITED',
    AF_PASSWORD_RECOVERY_PASSWORD_MISMATCH = 'AF_PASSWORD_RECOVERY_PASSWORD_MISMATCH',
    AF_PASSWORD_RECOVERY_PASSWORD_VALIDATION_FAILED = 'AF_PASSWORD_RECOVERY_PASSWORD_VALIDATION_FAILED',
    AF_PASSWORD_RECOVERY_TOKEN_INVALID = 'AF_PASSWORD_RECOVERY_TOKEN_INVALID',
    AF_PASSWORD_RECOVERY_TOKEN_EXPIRED = 'AF_PASSWORD_RECOVERY_TOKEN_EXPIRED',
    AF_SUPERSET_DASHBOARD_DUPLICATE = 'AF_SUPERSET_DASHBOARD_DUPLICATE',
    AF_SUPERSET_DASHBOARD_INVALID_ROLE = 'AF_SUPERSET_DASHBOARD_INVALID_ROLE',
    AF_TRANSACTION_USED_IN_REFUND = 'AF_TRANSACTION_USED_IN_REFUND',
    AF_INCIDENT_MSISDN_FRAUD_ENTRY_REQUIRED = 'AF_INCIDENT_MSISDN_FRAUD_ENTRY_REQUIRED',
    AF_REFUND_REJECTION_BIOMETRIC_FAILED = 'AF_REFUND_REJECTION_BIOMETRIC_FAILED',
    AF_REFUND_REJECTION_IIN_NOT_AVAILABLE = 'AF_REFUND_REJECTION_IIN_NOT_AVAILABLE',
    AF_REFUND_REJECTION_PHONE_NOT_AVAILABLE = 'AF_REFUND_REJECTION_PHONE_NOT_AVAILABLE',

    // mock bank
    BANK_NOT_FOUND = 'BANK_NOT_FOUND',
}
